import React, { useState, useMemo, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, ChevronDown, X } from 'lucide-react';
import { hybridAccountService } from '../../services/hybrid-account-service';
import type { Account } from '../../types/account.types';
import { AccountType } from '../../types/account.types';
import clsx from 'clsx';

interface AccountFilterDropdownProps {
  value: string | undefined;
  valueIds?: string[]; // Multiple account IDs (takes precedence over value)
  onChange: (accountId: string | undefined) => void;
  onChangeIds?: (accountIds: string[]) => void; // For multiple selections
  className?: string;
}

export function AccountFilterDropdown({
  value,
  valueIds,
  onChange,
  onChangeIds,
  className
}: AccountFilterDropdownProps): React.ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Fetch accounts (excluding assets as they don't have transactions)
  useEffect(() => {
    const loadAccounts = async () => {
      setLoading(true);
      try {
        const result = await hybridAccountService.getAccounts({ status: 'active' });
        if (result.data) {
          // Filter out assets since they don't have transactions
          const filteredAccounts = result.data.accounts.filter(
            account => account.type !== AccountType.ASSETS
          );
          setAccounts(filteredAccounts);
        }
      } catch (error) {
        console.error('Failed to load accounts:', error);
      } finally {
        setLoading(false);
      }
    };
    loadAccounts();
  }, []);

  // Group accounts by parent account type
  const getParentAccountType = (accountType: AccountType): string => {
    switch (accountType) {
      case AccountType.BANK_CASH:
        return 'Everyday';
      case AccountType.CREDIT_CARD:
      case AccountType.LOAN:
        return 'Liabilities';
      case AccountType.SAVINGS:
        return 'Savings';
      case AccountType.INVESTMENTS_SUPERANNUATION:
        return 'Investments';
      default:
        return 'Other';
    }
  };

  // Group accounts by parent type
  const accountsByParentType = useMemo(() => {
    const grouped = new Map<string, Account[]>();
    
    accounts.forEach(account => {
      const parentType = getParentAccountType(account.type);
      if (!grouped.has(parentType)) {
        grouped.set(parentType, []);
      }
      grouped.get(parentType)!.push(account);
    });

    // Sort accounts within each group by name
    grouped.forEach((accountList) => {
      accountList.sort((a, b) => a.name.localeCompare(b.name));
    });

    // Sort groups in a specific order: Everyday, Liabilities, Savings, Investments
    const groupOrder = ['Everyday', 'Liabilities', 'Savings', 'Investments', 'Other'];
    const sortedGroups = new Map<string, Account[]>();
    groupOrder.forEach(type => {
      if (grouped.has(type)) {
        sortedGroups.set(type, grouped.get(type)!);
      }
    });

    return sortedGroups;
  }, [accounts]);

  // Filter accounts based on search query
  const filteredAccountsByParentType = useMemo(() => {
    if (!searchQuery.trim()) {
      return accountsByParentType;
    }

    const query = searchQuery.toLowerCase();
    const filtered = new Map<string, Account[]>();

    accountsByParentType.forEach((accountList, parentType) => {
      const matchingAccounts = accountList.filter(account =>
        account.name.toLowerCase().includes(query) ||
        account.provider.toLowerCase().includes(query) ||
        parentType.toLowerCase().includes(query) ||
        account.type.toLowerCase().includes(query)
      );
      
      if (matchingAccounts.length > 0) {
        filtered.set(parentType, matchingAccounts);
      }
    });

    return filtered;
  }, [accountsByParentType, searchQuery]);

  // Get selected account name(s)
  const selectedAccountName = useMemo(() => {
    // If multiple account IDs are provided, show a summary
    if (valueIds && valueIds.length > 0) {
      if (valueIds.length === 1) {
        const account = accounts.find(acc => acc.id === valueIds[0]);
        return account?.name || 'Selected Account';
      }
      // Group by parent type to show a better summary
      const selectedAccounts = accounts.filter(acc => valueIds.includes(acc.id));
      const groupedByType = new Map<string, number>();
      selectedAccounts.forEach(acc => {
        const parentType = getParentAccountType(acc.type);
        groupedByType.set(parentType, (groupedByType.get(parentType) || 0) + 1);
      });
      
      // If all selected accounts are the same type, show that type
      if (groupedByType.size === 1) {
        const [type, count] = Array.from(groupedByType.entries())[0];
        return `${count} ${type} Account${count > 1 ? 's' : ''}`;
      }
      
      // Otherwise show total count
      return `${valueIds.length} Accounts Selected`;
    }
    
    // Single account selection
    if (!value) return 'All Accounts';
    const account = accounts.find(acc => acc.id === value);
    return account?.name || 'All Accounts';
  }, [value, valueIds, accounts]);

  const updateMenuPosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const spaceBelow = viewportH - rect.bottom;
    const menuHeight = 400;
    const openUp = spaceBelow < menuHeight && rect.top > spaceBelow;
    const maxHeight = Math.min(384, openUp ? rect.top - 12 : spaceBelow - 12);

    setMenuStyle({
      position: 'fixed',
      left: rect.left,
      width: Math.max(rect.width, 320),
      maxHeight: Math.max(maxHeight, 200),
      zIndex: 100,
      ...(openUp
        ? { bottom: viewportH - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    updateMenuPosition();
    searchInputRef.current?.focus({ preventScroll: true });
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [isOpen, updateMenuPosition]);

  const closeMenu = () => {
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleSelect = (accountId: string | undefined) => {
    onChange(accountId);
    closeMenu();
  };

  const menu =
    isOpen && typeof document !== 'undefined'
      ? createPortal(
          <>
            <div className="fixed inset-0 z-[99]" onClick={closeMenu} aria-hidden="true" />
            <div
              ref={menuRef}
              style={menuStyle}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl overflow-hidden flex flex-col"
            >
              {/* Search Box */}
              <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search accounts..."
                    className="w-full pl-10 pr-10 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      aria-label="Clear search"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Account List */}
              <div className="overflow-y-auto flex-1 min-h-0">
                {loading ? (
                  <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    Loading accounts...
                  </div>
                ) : filteredAccountsByParentType.size === 0 ? (
                  <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    No accounts found
                  </div>
                ) : (
                  <>
                    {/* All Accounts Option */}
                    <label
                      className={clsx(
                        'w-full px-4 py-2.5 text-sm transition-colors flex items-center justify-between cursor-pointer',
                        (!value && (!valueIds || valueIds.length === 0))
                          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          checked={!value && (!valueIds || valueIds.length === 0)}
                          onChange={() => {
                            if (onChangeIds) {
                              onChangeIds([]);
                            } else {
                              handleSelect(undefined);
                            }
                          }}
                          aria-label="Select all accounts"
                        />
                        <span>All Accounts</span>
                      </div>
                      {!value && (!valueIds || valueIds.length === 0) && (
                        <span className="text-xs font-semibold text-primary-700 dark:text-primary-300">
                          Selected
                        </span>
                      )}
                    </label>

                    {/* Account Groups by Parent Type */}
                    {Array.from(filteredAccountsByParentType.entries()).map(([parentType, accountList]) => (
                      <div key={parentType} className="border-t border-gray-100 dark:border-gray-700">
                        {/* Parent Type Header */}
                        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900/50 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          {parentType}
                        </div>

                        {/* Accounts in this type */}
                        {accountList.map((account) => {
                          const isSelected = onChangeIds
                            ? (valueIds || []).includes(account.id)
                            : value === account.id;
                          return (
                            <label
                              key={account.id}
                              className={clsx(
                                'w-full px-4 py-2.5 text-sm transition-colors flex items-center gap-3 cursor-pointer',
                                isSelected
                                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                              )}
                            >
                              <input
                                type="checkbox"
                                className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                checked={isSelected}
                                onChange={() => {
                                  if (onChangeIds) {
                                    const currentIds = valueIds || [];
                                    const newIds = isSelected
                                      ? currentIds.filter((id) => id !== account.id)
                                      : [...currentIds, account.id];
                                    onChangeIds(newIds);
                                  } else {
                                    handleSelect(account.id);
                                  }
                                }}
                                aria-label={`Select ${account.name}`}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{account.name}</div>
                                {account.provider && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                    {account.provider}
                                  </div>
                                )}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          </>,
          document.body
        )
      : null;

  return (
    <div className={clsx('relative', className)}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary-500 focus:border-primary-500 min-w-[180px]"
        aria-label="Filter by account"
        aria-expanded={isOpen}
      >
        <span className="text-sm truncate">{selectedAccountName}</span>
        <ChevronDown
          className={clsx(
            'h-4 w-4 ml-2 flex-shrink-0 transition-transform',
            isOpen && 'rotate-180'
          )}
          aria-hidden="true"
        />
      </button>
      {menu}
    </div>
  );
}

